// External libraries
import { useMemo, useRef } from 'react';
import { useStoreActions, useStoreState } from '@/store';

// Internal modules
import { CI, features } from '@/lib';
import {
  logBankPaymentSuppressionActiveToFpti,
  logBankPaymentSuppressionErrorToFpti,
  logFiTabClickEventToFpti,
  logPrepareTabbedViewToFpti,
} from '../fpti';
import { ConsumerAddBankForm } from '@/components/ConsumerAddBankForm';
import { PayNowForm } from '../PayNowForm';
import { PayLaterForm } from '../PayLaterForm';
import { logInteraction } from '../TabsComponent/utils';
import { FormTabs } from '@/types';
import type { FormListItem } from '../types';
import { getFormTabContent } from '../utils';

interface UseFormListReturn {
  formList: FormListItem[];
  initialSelectedTabIndex: number;
  onAfterTabChange: (index: number) => void;
  tabList: { id: string; label: string; panelId: string }[];
}

export const useFormList = (): UseFormListReturn => {
  // State
  const fiType = useStoreState(({ ui }) => ui.fiType);
  const showTabs = useStoreState(({ ui }) => ui.showTabs);
  const { value: onboardOption } = useStoreState(
    ({ form }) => form.onboardOption,
  );
  const savedOnboardOption = useRef(onboardOption);

  // Features
  const hasBank = features.someActive('hasAddBank', 'hasConsumerAddBank');
  const hasConsumerAddBank = features.isActive('hasConsumerAddBank');
  const bnplExperience = features.get('bnplExperience');
  const hasPayLaterTab = bnplExperience === 'TAB';
  const isGooglePayLaterOnly = features.isActive('isGooglePayLaterOnly');

  // Actions
  const setFiType = useStoreActions(({ ui }) => ui.setFiType);
  const setPageLevelError = useStoreActions(({ ui }) => ui.setPageLevelError);
  const updateOnboardOption = useStoreActions(
    ({ form }) => form.updateOnboardOption,
  );

  // Features
  const isBankPaymentsSuppressed = features.isActive(
    'hasSuppressBankFundedGuestPayments',
  );

  // Form Configuration
  const formList = useMemo(() => {
    const forms: FormListItem[] = [
      // Bank Tab
      {
        fiType: 'bank',
        form: hasConsumerAddBank ? ConsumerAddBankForm : PayNowForm,
        fptiName: 'pay_now_bank',
        // Suppress bank tab if bank feature is off OR if this is Google Pay Later only flow
        inactive: !hasBank || isGooglePayLaterOnly,
        interaction: CI.Event.PAY_BANK_RENDER,
        tabId: FormTabs.Bank,
      },
      // Card (Pay Now) Tab
      {
        fiType: 'card',
        form: PayNowForm,
        fptiName: 'pay_now_card',
        // Suppress card tab entirely for Google Pay Later only flow
        inactive: isGooglePayLaterOnly,
        interaction: CI.Event.PAY_NOW_RENDER,
        tabId: FormTabs.Card,
      },
      // Pay Later (BNPL) Tab
      {
        fiType: 'bnpl',
        form: PayLaterForm,
        fptiName: 'pay_later',
        // Force bnpl active for Google Pay Later only flow, regardless of bnplExperience value.
        // Google sessions have no GPL credit eligibility configured, so bnplExperience returns ''
        // for these sessions. We override inactive here as defense-in-depth.
        inactive: !hasPayLaterTab && !isGooglePayLaterOnly,
        interaction: CI.Event.PAY_LATER_RENDER,
        tabId: FormTabs.BNPL,
      },
    ];
    return forms.filter(({ inactive }) => !inactive);
  }, []);

  const initialSelectedTabIndex = useMemo(() => {
    const index = formList.findIndex(e => e.fiType === fiType);
    const defaultTab = formList[index].fptiName;

    if (showTabs) {
      logPrepareTabbedViewToFpti({
        available_funding_options: bnplExperience ? 'pay_in_four' : '',
        available_tabs: formList.map(tab => tab.fptiName).join(),
        default_tab: defaultTab,
      });

      if (isBankPaymentsSuppressed) {
        logBankPaymentSuppressionActiveToFpti(defaultTab);
      }
    }

    return index;
  }, []);

  const onAfterTabChange = (index: number) => {
    if (index === undefined) {
      CI.fci();
    } else {
      const currentTab = formList[index];
      // Log customer interaction on tab click and keyboard "Enter" key press
      logInteraction(currentTab.interaction, currentTab.fiType, fiType);
    }

    const newFiType = formList[index].fiType;

    if (!(fiType === newFiType)) {
      logFiTabClickEventToFpti(formList[index].fptiName);
      setFiType(newFiType);

      // Show error when switching to bank tab
      // if bank payments are suppressed (DE fraud mitigation)
      if (isBankPaymentsSuppressed) {
        if (newFiType === 'bank') {
          logBankPaymentSuppressionErrorToFpti();
          setPageLevelError('pageLevelError.cannotUseBank');
        } else {
          setPageLevelError('');
        }
      }

      // A Pay Later buyer must sign up before continuing their transaction
      // as a member, so we set onboardOption when they select this tab.
      // If they move off this tab, we reset onboardOption to what it was
      // before, to restore the state of the signup checkbox / radio buttons.
      if (newFiType === 'bnpl') {
        savedOnboardOption.current = onboardOption;
        updateOnboardOption('signup');
      } else if (fiType === 'bnpl') {
        updateOnboardOption(savedOnboardOption.current);
      }
    }
  };

  const tabList = formList.map(tabObject => ({
    id: tabObject.tabId,
    label: getFormTabContent(tabObject.fiType).title as string,
    panelId: tabObject.tabId,
  }));

  return {
    formList,
    initialSelectedTabIndex,
    onAfterTabChange,
    tabList,
  };
};
